import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Animated, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import { ArrowLeftIcon, BookOpenIcon, ShieldCheckIcon, SpinnerGapIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import {
  RubricParseStatus,
  type ActiveRubricSetQuery,
  type InstitutionalGuidelinesBySchoolQuery,
  type RubricSetsBySchoolQuery,
  type RubricSetParseStatusQuery,
} from "@axio/graphql/generated/graphql";
import AdminTabNavigator from "../../AdminTabNavigator/AdminTabNavigator";
import GuidelinesTab, { type GuidelineUploadState } from "./GuidelinesTab";
import RubricProcessing from "./RubricProcessing";
import RubricReady, { RubricReadyFooter, type RubricCriterionPatch, type RubricSectionPatch } from "./RubricReady";
import RubricUpload, { type RubricUploadState } from "./RubricUpload";

type RubricSetViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSetStatusViewModel = NonNullable<RubricSetParseStatusQuery["rubricSet"]["data"]>;
type AvailableRubricViewModel = RubricSetsBySchoolQuery["rubricSetsBySchool"]["data"][number];
type RubricDisplayViewModel = RubricSetViewModel | RubricSetStatusViewModel;
type GuidelineViewModel = InstitutionalGuidelinesBySchoolQuery["institutionalGuidelinesBySchool"]["data"][number];

interface RubricConfigSlideInProps {
  visible: boolean;
  onClose: () => void;
  rubric: RubricDisplayViewModel | null;
  availableRubrics: AvailableRubricViewModel[];
  loadingRubric: boolean;
  loadingAvailableRubrics: boolean;
  savingRubric: boolean;
  activatingRubricId: string | null;
  selectedFileName: string | null;
  selectedFileSize: number | null;
  uploadState: RubricUploadState;
  uploadError: string | null;
  allowedMimeTypes: string[];
  maxFileSize: number;
  onSelectFile: () => void;
  onClearFile: () => void;
  onParseRubric: () => void;
  onUpdateRubric: (input: { sections?: RubricSectionPatch[]; criteria?: RubricCriterionPatch[] }) => void;
  onActivateRubric: (rubricId: string) => void;
  showParsedRubricPreview: boolean;
  guidelines: GuidelineViewModel[];
  loadingGuidelines: boolean;
  selectedGuidelineFileName: string | null;
  selectedGuidelineFileSize: number | null;
  guidelineUploadState: GuidelineUploadState;
  guidelineUploadError: string | null;
  deletingGuidelineId: string | null;
  onSelectGuidelineFile: () => void;
  onClearGuidelineFile: () => void;
  onCreateGuideline: () => Promise<boolean> | boolean;
  onDeleteGuideline: (guidelineId: string) => void;
}

const inProgressStatuses = [RubricParseStatus.Pending, RubricParseStatus.Parsing];

const hasRubricSections = (rubric: RubricDisplayViewModel): rubric is RubricSetViewModel => "sections" in rubric;

function RubricConfigSlideIn({
  visible,
  onClose,
  rubric,
  availableRubrics,
  loadingRubric,
  loadingAvailableRubrics,
  savingRubric,
  activatingRubricId,
  selectedFileName,
  selectedFileSize,
  uploadState,
  uploadError,
  allowedMimeTypes,
  maxFileSize,
  onSelectFile,
  onClearFile,
  onParseRubric,
  onUpdateRubric,
  onActivateRubric,
  showParsedRubricPreview,
  guidelines,
  loadingGuidelines,
  selectedGuidelineFileName,
  selectedGuidelineFileSize,
  guidelineUploadState,
  guidelineUploadError,
  deletingGuidelineId,
  onSelectGuidelineFile,
  onClearGuidelineFile,
  onCreateGuideline,
  onDeleteGuideline,
}: RubricConfigSlideInProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [showUploadView, setShowUploadView] = useState(false);
  const [activeTab, setActiveTab] = useState("rubric");
  const { width } = useWindowDimensions();
  const panelWidth = Math.min(480, width);
  const slideAnim = useRef(new Animated.Value(panelWidth)).current;

  useEffect(() => {
    if (visible) {
      setIsMounted(true);
      setShowUploadView(false);
      setActiveTab("rubric");
    }
    Animated.timing(slideAnim, {
      toValue: visible ? 0 : panelWidth,
      duration: 300,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!visible && finished) {
        setIsMounted(false);
      }
    });
  }, [panelWidth, slideAnim, visible]);

  useEffect(() => {
    if (uploadError) {
      setShowUploadView(true);
    }
  }, [uploadError]);

  if (!isMounted) {
    return null;
  }

  const showReadyRubric =
    !showUploadView && rubric?.parseStatus === RubricParseStatus.Ready && !showParsedRubricPreview;
  const showCreatingRubric = uploadState === "creating" && !showUploadView;
  const showProcessingRubric =
    !showUploadView &&
    (showCreatingRubric ||
      showParsedRubricPreview ||
      (rubric != null && inProgressStatuses.includes(rubric.parseStatus)));
  const showFailedRubric = rubric?.parseStatus === RubricParseStatus.Failed;
  const showUpload = showUploadView || (!showReadyRubric && !showProcessingRubric);
  const hideHeader = showUploadView && rubric?.parseStatus === RubricParseStatus.Ready;
  const canUploadGuidelines = rubric?.parseStatus === RubricParseStatus.Ready;
  const tabData = [
    { key: "rubric", title: "Rubric", icon: <ShieldCheckIcon size={20} weight="regular" /> },
    {
      key: "guidelines",
      title: guidelines.length > 0 ? `Guidelines (${guidelines.length})` : "Guidelines",
      icon: <BookOpenIcon size={20} weight="regular" />,
    },
  ];
  const handleParseRubric = () => {
    setShowUploadView(false);
    onParseRubric();
  };

  const panelContent = (
    <Animated.View style={[styles.panel, { width: panelWidth, transform: [{ translateX: slideAnim }] }]}>
      <View style={styles.panelContainer}>
        {hideHeader ? null : (
          <View style={styles.topRow}>
            <View style={styles.headerCopy}>
              <CustomText
                text="Evaluation Configuration"
                size="xl"
                weight="bold"
                style={styles.headerText}
                useTranslationText={false}
              />
              <CustomText
                text="Rubric, guidelines, and policies used during course evaluation"
                size="s"
                style={styles.secondaryText}
                useTranslationText={false}
              />
            </View>
            <Pressable
              role="button"
              aria-label="Close evaluation configuration"
              style={styles.iconButton}
              onPress={onClose}>
              <XIcon color="#344054" size={16} />
            </Pressable>
          </View>
        )}

        <View style={styles.contentContainer}>
          {!showUploadView ? (
            <View style={styles.tabsWrapper}>
              <AdminTabNavigator
                tabData={tabData}
                activeTab={activeTab}
                onTabPress={setActiveTab}
                navBarStyle={styles.tabNavBar}>
                {(selectedTab) =>
                  selectedTab === "guidelines" ? (
                    <GuidelinesTab
                      guidelines={guidelines}
                      loadingGuidelines={loadingGuidelines}
                      canUploadGuidelines={canUploadGuidelines}
                      selectedFileName={selectedGuidelineFileName}
                      selectedFileSize={selectedGuidelineFileSize}
                      uploadState={guidelineUploadState}
                      uploadError={guidelineUploadError}
                      deletingGuidelineId={deletingGuidelineId}
                      onSelectFile={onSelectGuidelineFile}
                      onClearFile={onClearGuidelineFile}
                      onCreateGuideline={onCreateGuideline}
                      onDeleteGuideline={onDeleteGuideline}
                    />
                  ) : loadingRubric ? (
                    <ScrollView style={styles.scrollContent}>
                      <View style={styles.contentInner}>
                        <View style={styles.processingPanel}>
                          <SpinnerGapIcon color="#3E68FE" size={28} weight="bold" />
                          <CustomText text="Loading rubric..." size="m" weight="bold" useTranslationText={false} />
                        </View>
                      </View>
                    </ScrollView>
                  ) : showReadyRubric && rubric && hasRubricSections(rubric) ? (
                    <View style={styles.bodyColumn}>
                      <ScrollView style={styles.scrollContent}>
                        <RubricReady
                          rubric={rubric}
                          availableRubrics={availableRubrics}
                          loadingAvailableRubrics={loadingAvailableRubrics}
                          savingRubric={savingRubric}
                          activatingRubricId={activatingRubricId}
                          onUpdateRubric={onUpdateRubric}
                          onActivateRubric={onActivateRubric}
                        />
                      </ScrollView>
                      <RubricReadyFooter
                        rubric={rubric}
                        savingRubric={savingRubric}
                        onUpdateRubric={onUpdateRubric}
                        onReplaceRubric={() => setShowUploadView(true)}
                      />
                    </View>
                  ) : showProcessingRubric ? (
                    <ScrollView style={styles.scrollContent}>
                      <View style={styles.contentInner}>
                        <RubricProcessing rubric={rubric} selectedFileName={selectedFileName} />
                      </View>
                    </ScrollView>
                  ) : showUpload ? (
                    <ScrollView style={styles.scrollContent}>
                      <View style={styles.contentInner}>
                        {showFailedRubric && rubric ? <RubricProcessing rubric={rubric} /> : null}
                        <RubricUpload
                          selectedFileName={selectedFileName}
                          selectedFileSize={selectedFileSize}
                          uploadState={uploadState}
                          uploadError={uploadError}
                          allowedMimeTypes={allowedMimeTypes}
                          maxFileSize={maxFileSize}
                          onSelectFile={onSelectFile}
                          onClearFile={onClearFile}
                          onParseRubric={handleParseRubric}
                        />
                      </View>
                    </ScrollView>
                  ) : null
                }
              </AdminTabNavigator>
            </View>
          ) : (
            <ScrollView style={styles.scrollContent}>
              <View style={styles.contentInner}>
                {rubric?.parseStatus === RubricParseStatus.Ready ? (
                  <Pressable
                    role="button"
                    accessibilityLabel="Back to rubric configuration"
                    onPress={() => setShowUploadView(false)}
                    style={styles.backButton}>
                    <ArrowLeftIcon color="#344054" size={14} weight="bold" />
                    <CustomText
                      text="Back to configuration"
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                    />
                  </Pressable>
                ) : null}
                {showFailedRubric && rubric ? <RubricProcessing rubric={rubric} /> : null}
                <RubricUpload
                  selectedFileName={selectedFileName}
                  selectedFileSize={selectedFileSize}
                  uploadState={uploadState}
                  uploadError={uploadError}
                  allowedMimeTypes={allowedMimeTypes}
                  maxFileSize={maxFileSize}
                  onSelectFile={onSelectFile}
                  onClearFile={onClearFile}
                  onParseRubric={handleParseRubric}
                />
              </View>
            </ScrollView>
          )}
        </View>
      </View>
    </Animated.View>
  );

  return createPortal(panelContent, document.body);
}

const styles = StyleSheet.create({
  panel: {
    // @ts-expect-error - react-native-web accepts fixed positioning
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    // @ts-expect-error - react-native-web accepts viewport units
    height: "100vh",
  },
  panelContainer: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#EAECF0",
    overflow: "hidden",
  },
  topRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 16,
  },
  headerText: {
    color: "#101828",
    fontSize: 20,
    lineHeight: 30,
  },
  headerCopy: {
    flex: 1,
    gap: 2,
  },
  iconButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    minHeight: 36,
    padding: 8,
    borderRadius: 8,
  },
  contentContainer: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  bodyColumn: {
    flex: 1,
    minHeight: 0,
  },
  tabsWrapper: {
    flex: 1,
    minHeight: 0,
    paddingTop: 16,
  },
  tabNavBar: {
    marginHorizontal: 24,
  },
  scrollContent: {
    flex: 1,
    minHeight: 0,
  },
  contentInner: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  secondaryText: {
    color: "#344054",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    minHeight: 32,
    marginBottom: 12,
  },
  processingPanel: {
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 260,
    paddingHorizontal: 20,
    paddingVertical: 28,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
});

export default RubricConfigSlideIn;
